<template>
  <div class="tw-mb-8">
    <!-- Header section with click handler -->
    <div
      class="tw-w-full tw-bg-gray-100 tw-p-4 lg:tw-p-8 tw-rounded-t-md hover:tw-bg-gray-200 tw-border-l-8 tw-border-brand-orange tw-border-solid tw-cursor-pointer tw-transition-colors tw-duration-300"
      @click="toggleOpen"
    >
      <div class="tw-flex tw-justify-between tw-items-center tw-space-x-4">
        <div class="tw-flex tw-items-center">
          <!-- Conditional image container with border -->
          <div
            v-if="
              solicitation.featured_image && solicitation.featured_image.url
            "
            class="tw-mr-2 tw-border-r tw-border-gray-400 tw-border-solid"
          >
            <img
              :src="
                solicitation.featured_image.medium ||
                solicitation.featured_image.url
              "
              :alt="solicitation.featured_image.alt || solicitation.title"
              class="tw-w-[100px] tw-mr-4"
            />
          </div>
          <!-- Fallback to jwbflex if no featured image -->

          <div
            :class="{
              'tw-ml-2':
                solicitation.featured_image && solicitation.featured_image.url,
            }"
          >
            <h4 class="tw-text-md">{{ solicitation.title }}</h4>
            <div class="lg:tw-flex tw-items-center lg:tw-space-x-6">
              <h3 class="tw-text-base tw-font-bold">
                Status: {{ solicitation.capitalizedType }}
              </h3>
              <div class="tw-flex tw-items-center">
                <h5 class="tw-font-semibold tw-text-base tw-mr-2">Due Date:</h5>
                <div
                  v-if="solicitation.due_date || solicitation.due_time"
                  class="tw-relative tw-top-[1px] tw-flex"
                >
                  <p
                    v-if="solicitation.due_date"
                    class="tw-m-0 tw-text-sm tw-mr-2"
                  >
                    {{ solicitation.due_date }}
                  </p>
                  <p v-if="solicitation.due_time" class="tw-m-0 tw-text-sm">
                    {{ solicitation.due_time }} EST
                  </p>
                </div>
                <p v-else class="tw-text-sm tw-m-0">TBD</p>
              </div>
            </div>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="tw-size-6 tw-transition-transform tw-duration-300 tw-flex-shrink-0"
          :class="{ 'tw-rotate-180 tw-transform': isOpen }"
        >
          <path
            fill-rule="evenodd"
            d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </div>

    <!-- Content section with Tailwind transitions -->
    <div
      class="tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out tw-max-h-0 tw-bg-gray-50 tw-rounded-b-md"
      :class="{ 'tw-max-h-[2000px]': isOpen }"
      ref="content"
    >
      <div class="tw-p-8">
        <div
          v-if="solicitation?.content"
          v-html="solicitation?.content"
          class="tw-mb-8"
        ></div>

        <h5 class="tw-font-semibold tw-mt-4 tw-mb-2">
          {{ solicitation.pre_bid_conference_title || 'Pre-bid Conference' }}
        </h5>
        <div
          v-if="
            solicitation.pre_bid_conferences &&
            solicitation.pre_bid_conferences.length > 0
          "
        >
          <div
            v-for="conference in solicitation.pre_bid_conferences"
            :key="conference.date"
            class="tw-mb-8"
          >
            <p v-if="conference.date" class="tw-m-0 tw-text-sm">
              {{ conference.date }}
            </p>

            <p
              v-if="conference.start_time"
              class="tw-uppercase tw-m-0 tw-text-sm"
            >
              {{ conference.start_time }} EST
              <template v-if="conference.end_time">
                - {{ conference.end_time }} EST
              </template>
            </p>

            <p v-if="conference.location" class="tw-m-0 tw-text-sm">
              {{ conference.location }}
            </p>
            <p v-if="conference.location" class="tw-m-0 tw-text-sm">
              {{ conference.location_in_person_virtual_etc }}
            </p>
          </div>
        </div>
        <p v-else class="tw-text-sm tw-m-0">TBD</p>

        <div class="tw-mb-8">
          <h5 class="tw-font-semibold tw-mt-4 tw-mb-2">Intent to Award</h5>
          <p v-if="solicitation.intent_to_award_date" class="tw-m-0 tw-text-sm">
            {{ solicitation.intent_to_award_date }}
          </p>
          <p v-else class="tw-text-sm tw-m-0">TBD</p>
          <div v-if="solicitation.intent_to_award_document" class="tw-mt-2">
            <a
              :href="solicitation.intent_to_award_document.url"
              target="_blank"
              class="tw-text-blue-600 hover:tw-underline"
              >Download Document</a
            >
          </div>
        </div>

        <div class="tw-mb-8">
          <h5 class="tw-font-semibold tw-mt-4 tw-mb-2">Award Date</h5>
          <p class="tw-m-0 tw-text-sm">
            {{ solicitation.award_date || 'TBD' }}
          </p>
        </div>

        <div class="tw-mb-8">
          <h5 class="tw-font-semibold tw-mt-4 tw-mb-2">Award Notice</h5>
          <template v-if="solicitation.award_notice_link">
            <p class="tw-m-0 tw-text-sm">
              <a
                :href="solicitation.award_notice_link"
                class="tw-text-blue-600 hover:tw-underline"
                >Click Here</a
              >
            </p>
          </template>
          <template
            v-else-if="
              solicitation.award_notice_date || solicitation.award_notice_time
            "
          >
            <p v-if="solicitation.award_notice_date" class="tw-m-0 tw-text-sm">
              {{ solicitation.award_notice_date }}
            </p>
            <p v-if="solicitation.award_notice_time" class="tw-m-0 tw-text-sm">
              {{ solicitation.award_notice_time }} EST
            </p>
          </template>
          <p v-else class="tw-m-0 tw-text-sm">TBD</p>
          <div v-if="solicitation.award_document" class="tw-mt-2">
            <a
              :href="solicitation.award_document.url"
              target="_blank"
              class="tw-text-blue-600 hover:tw-underline"
              >Download Document</a
            >
          </div>
        </div>

        <!-- <div class="tw-mb-8">
          <h5 class="tw-font-semibold tw-mt-4 tw-mb-2">Results</h5>
          <p v-if="solicitation.result" class="tw-m-0 tw-text-sm">
            <a
              :href="solicitation.result"
              target="_blank"
              class="tw-text-blue-600 hover:tw-underline"
              >Solicitation Documents</a
            >
          </p>
          <p v-else class="tw-m-0 tw-text-sm">TBD</p>
        </div> -->
        <!-- <div
          v-if="solicitation.files && solicitation.files.length > 0"
          class="tw-mt-2"
        >
          <a
            :href="'#' + solicitation.id"
            class="tw-inline-block tw-px-3 tw-py-1 tw-bg-blue-600 tw-text-white tw-rounded-md"
            >View Files</a
          >
        </div> -->
        <div v-if="solicitation?.learn_more_link?.url" class="tw-mt-6">
          <a
            v-if="solicitation?.learn_more_link?.url"
            :href="solicitation?.learn_more_link?.url"
            class="tw-inline-flex tw-items-center tw-font-bold tw-font-sans hover:tw-text-white tw-px-6 tw-py-3 tw-bg-brand-orange tw-text-white tw-rounded-full tw-mt-4"
          >
            Click here to learn more and apply</a
          >
          <!-- <h4>Questions? Email help@jwbpinellas.org</h4> -->
        </div>
      </div>
    </div>

    <!-- Lightbox for files -->
    <div
      v-if="solicitation.files && solicitation.files.length > 0"
      :id="solicitation.id"
      class="tw-hidden tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-70 tw-z-50 target:tw-flex tw-items-center tw-justify-center"
    >
      <div
        class="tw-relative tw-max-w-xl tw-w-11/12 tw-bg-white tw-rounded-lg tw-p-5 tw-max-h-[80vh] tw-overflow-y-auto"
      >
        <a
          class="tw-absolute tw-top-3 tw-right-4 tw-text-xl tw-no-underline tw-text-gray-700"
          href="#close"
          >&Cross;</a
        >
        <div class="tw-p-4">
          <h4 class="tw-text-lg tw-font-bold tw-mb-4">
            Files for {{ solicitation.title }}
          </h4>
          <ul class="tw-list-disc tw-pl-5">
            <li
              v-for="file in solicitation.files"
              :key="file.file"
              class="tw-mb-2"
            >
              <a
                :href="file.file"
                target="_blank"
                class="tw-text-blue-600 hover:tw-underline"
              >
                {{ file.file_name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import jwbflex from '@/assets/jwbflex.png'
export default {
  name: 'SolicitationItem',
  props: {
    solicitation: {
      type: Object,
      required: true,
    },
    initialOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: this.initialOpen,
      jwbflex,
    }
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen
    },
  },
}
</script>
